import {
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  InputSelect,
} from '@features/ui';
import { EditorComponent } from '@features/admin/emails/EditorComponent';
import { FC, useCallback, useEffect } from 'react';
import { EmailFormData, schemaEmail } from '@src/types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getEmailDetail,
  saveEmails,
  sendTestEmail,
} from '@src/services/emails.ts';
import { toast } from 'react-toastify';

interface EmailDetailProps {
  isEdit?: boolean;
}

export const EmailDetail: FC<EmailDetailProps> = ({ isEdit }) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EmailFormData>({
    resolver: yupResolver(schemaEmail),
  });
  const params = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEdit || !params?.id) return;
    getEmailDetail(Number(params?.id)).then(({ data }) => {
      reset({
        content: data.content,
        subject: data.subject,
        name: data.name,
        system_name: data.system_name ?? undefined,
        is_active: data.is_active ?? undefined,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, params?.id]);

  const handleSubmitForm = useCallback(
    async (data: EmailFormData) => {
      try {
        const payload = {
          id: isEdit && params.id ? Number(params.id) : undefined,
          ...data,
        };

        await new Promise((resolve) => setTimeout(resolve, 500));

        await saveEmails(payload);

        if (!isEdit) {
          navigate(`/admin/emails`);
        }
      } catch {
        toast.error('Something went wrong. Please try again later.');
      }
    },
    [isEdit, navigate, params.id]
  );

  const handleSendTestEmail = useCallback(async () => {
    if (!params?.id) return;
    await sendTestEmail(Number(params.id));
  }, [params?.id]);

  return (
    <form autoComplete="off" onSubmit={handleSubmit(handleSubmitForm)}>
      <div className={'flex justify-between items-center'}>
        <div className={'flex gap-x-2 text-[23px]'}>
          <p className={'text-blue'}>Email Template</p>
          <p>|</p>
          <p>{`${isEdit ? 'Edit' : 'Create'} Email Template`}</p>
        </div>
        <Button
          className={'rounded-[2px]'}
          loading={isSubmitting}
          size={'sm'}
          type={'submit'}
          variant={'lime'}
        >
          Save
        </Button>
      </div>
      <Card className={'mt-4.5 mb-5 text-xs space-y-7'}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <div className={'flex flex-col gap-y-1'}>
              <Label className={'text-xs mb-1'}>Name</Label>
              <Input
                placeholder={'Enter'}
                textSize={'xs'}
                variant={'secondary'}
                {...field}
              />
              <p className={'text-red-500'}>{errors.name?.message}</p>
            </div>
          )}
        />
        <Controller
          control={control}
          name="system_name"
          render={({ field }) => {
            const email_template_system_name_options = [
              { value: 'new_user_rithmic', label: 'New User Welcome' },
              { value: 'leeloo_registration', label: 'Leeloo: Registration' },
              {
                value: 'new_account_rithmic',
                label: 'Account Activated (Rithmic)',
              },
              { value: 'pa_activated', label: 'Pro Account Activated' },
              { value: 'pa_closed', label: 'Pro Account Closed' },
              { value: 'customer_reset', label: 'Customer Reset Account' },
              { value: 'video_purchase', label: 'Customer Video Purchase' },
              { value: 'evaluation_passed', label: 'Evaluation Passed' },
              {
                value: 'evaluation_passed_reminder',
                label: 'Evaluation Passed Reminder',
              },
              { value: 'payout_request', label: 'Customer Payout Request' },
              { value: 'payout_complete', label: 'Customer Payout Complete' },
              { value: 'payout_cancelled', label: 'Customer Payout Cancelled' },
              {
                value: 'pandadoc_incomplete',
                label: 'Customer PandaDoc Missing Info',
              },
              { value: 'evaluation_passed_2', label: 'Evaluation Passed 2' },
              { value: 'pandadoc_signed', label: 'Pandadoc Signed v2' },
              {
                value: 'merchant_purchase',
                label: 'Merchant Product Purchase',
              },
              {
                value: 'merchant_approved',
                label: 'Merchant Product Approved',
              },
              { value: 'merchant_denied', label: 'Merchant Product Denied' },
              { value: 'payout_denied', label: 'Customer Payout Denied' },
              // {
              //   value: 'new_user_tradovate',
              //   label: 'New User Welcome Tradovate',
              // },
              // {
              //   value: 'new_account_tradovate',
              //   label: 'Account Activated (Tradovate)',
              // },
              {
                value: 'competition_payout_request',
                label: 'Competition Payout Request',
              },
              { value: 'addon_reset', label: 'Addon Reset Coupon Code' },
              { value: 'pa_deactivated', label: 'Pro Account Deactivated' },
              // { value: 'new_user_dxfeed', label: 'New User Welcome dxFeed' },
              // {
              //   value: 'new_account_dxfeed',
              //   label: 'Account Activated (dxFeed)',
              // },
              {
                value: 'new_affiliate_account',
                label: 'New Affiliate Account',
              },
            ];

            return (
              <div className={'flex flex-col gap-y-1'}>
                <Label className={'text-xs mb-1'}>Type</Label>
                <InputSelect
                  options={email_template_system_name_options}
                  placeholder={'Select...'}
                  value={field.value}
                  onChange={(val) => field.onChange(val)}
                />
              </div>
            );
          }}
        />
        <Controller
          control={control}
          name="subject"
          render={({ field }) => (
            <div className={'flex flex-col gap-y-1'}>
              <Label className={'text-xs mb-1'}>Subject</Label>
              <Input
                placeholder={'Enter'}
                textSize={'xs'}
                variant={'secondary'}
                {...field}
              />
              <p className={'text-red-500'}>{errors.subject?.message}</p>
            </div>
          )}
        />
      </Card>
      <Card className={'flex flex-col gap-y-5.5'}>
        <p className={'text-sm'}>Content</p>
        <Controller
          control={control}
          name="content"
          render={({ field }) => (
            <div className={'flex flex-col gap-y-1'}>
              <EditorComponent {...field} />
              {errors.subject?.message ? (
                <p className={'text-red-500 text-xs'}>
                  {errors.subject?.message}
                </p>
              ) : null}
            </div>
          )}
        />
        <Controller
          control={control}
          name="is_active"
          render={({ field }) => (
            <div className={'text-xs flex flex-col gap-y-2.5 font-semibold'}>
              <p>Published ?</p>
              <div className={'flex items-center gap-x-3'}>
                <Checkbox
                  checked={!!field.value}
                  className={'border-[white] border-[1px] h-5 w-5'}
                  label={'Yes'}
                  onCheckedChange={(value) => field.onChange(value)}
                />
              </div>
            </div>
          )}
        />
        <p className={'text-xs'}>
          Send a test email, save your template first!
        </p>
        <Button
          className={
            'border-[#B6D72C] hover:text-white w-fit rounded-[2px] text-xs mb-8.5'
          }
          size={'sm'}
          type={'button'}
          variant={'outline'}
          onClick={handleSendTestEmail}
        >
          Send Test
        </Button>
      </Card>
    </form>
  );
};
